/**
 * Centralized color constants for Technical Debt components
 * These values are used consistently across all Technical Debt views
 */

/**
 * Severity level colors
 * Using the most common values found across components
 */
export const SEVERITY_COLORS = {
  CRITICAL: '#c62828',  // Darker red (4.5:1 contrast) - most common
  HIGH: '#ef6c00',      // Darker orange (4.5:1 contrast) - most common
  MEDIUM: '#f9a825',    // Darker yellow (3:1 contrast) - most common
  LOW: '#43a047',       // Medium green (4.5:1 contrast) - most common
} as const;

/**
 * Solution status colors
 * Based on the most complete set found in Solutions component
 */
export const SOLUTION_STATUS_COLORS = {
  PENDING: '#f57c00',      // Orange
  APPROVED: '#4caf50',     // Light green
  IN_PROGRESS: '#0288d1',  // Blue
  COMPLETED: '#388e3c',    // Green
  REJECTED: '#d32f2f',     // Red
  ON_HOLD: '#fbc02d',      // Yellow
  CANCELLED: '#9e9e9e',    // Gray
} as const;

/**
 * Debt item status colors
 */
export const DEBT_STATUS_COLORS = {
  NOT_STARTED: '#9e9e9e',
  IN_PROGRESS: '#ff9800',
  RESOLVED: '#4caf50',
  "WON'T_FIX": '#f44336',
  SYSTEM_RESOLVED: '#4caf50',
} as const;

/**
 * Priority colors for solutions
 * These map priority levels to status colors for consistency
 */
export const PRIORITY_COLORS = {
  CRITICAL: '#d32f2f',  // REJECTED - Red
  HIGH: '#f57c00',      // PENDING - Orange
  MEDIUM: '#fbc02d',    // ON_HOLD - Yellow
  LOW: '#4caf50',       // APPROVED - Light green
} as const;

/**
 * Category colors for technical debt
 * Based on Dashboard component values (most comprehensive)
 */
export const CATEGORY_COLORS = {
  'DEBT-CODE': '#e91e63',                    // Pink
  'DEBT-SECURITY': '#3f51b5',                // Indigo
  'DEBT-TESTING': '#9c27b0',                 // Purple
  'DEBT-ARCHITECTURE': '#00bcd4',            // Cyan
  'DEBT-PERFORMANCE': '#ff9800',             // Orange
  'DEBT-DEPENDENCY-MANAGEMENT': '#4caf50',   // Green
  'DEBT-INFRASTRUCTURE': '#f44336',          // Red
} as const;

/**
 * Source colors for technical debt
 * Based on Dashboard component values
 */
export const SOURCE_COLORS = {
  'SONARQUBE': '#4caf50',   // Green
  'JIRA': '#ef6c00',        // Orange
  'DEPENDABOT': '#1976d2',  // Blue
  'GITHUB': '#9c27b0',      // Purple
  'MANUAL': '#ff9800',      // Amber
} as const;

/**
 * Chart colors for trend and metric charts
 */
export const CHART_COLORS = {
  primary: '#1976d2',
  secondary: '#dc004e',
  success: '#4caf50',
  warning: '#ff9800',
  error: '#f44336',
  info: '#00bcd4',
  purple: '#9c27b0',
  teal: '#009688',
  orange: '#ff5722',
  indigo: '#3f51b5',
  pink: '#e91e63',
  cyan: '#00acc1',
  amber: '#ffc107',
  lime: '#cddc39',
  deepOrange: '#ff6f00',
} as const;

/**
 * Color palette for dynamic assignment when values don't have predefined colors
 */
export const COLOR_PALETTE = [
  '#1976d2', // blue
  '#4caf50', // green
  '#f44336', // red
  '#ff9800', // orange
  '#9c27b0', // purple
  '#00bcd4', // cyan
  '#ff5722', // deep orange
  '#3f51b5', // indigo
  '#e91e63', // pink
  '#009688', // teal
  '#ffc107', // amber
  '#cddc39', // lime
  '#795548', // brown
  '#607d8b', // blue grey
  '#00acc1', // cyan accent
  '#8bc34a', // light green
] as const;

/**
 * Get color for a key with fallback to palette
 * @param key - Key to get color for
 * @param predefinedColors - Map of predefined colors (can be const object or Record)
 * @param allKeys - All available keys (for fallback calculation)
 * @returns Color string
 */
export const getColor = (
  key: string,
  predefinedColors: Record<string, string> | { readonly [key: string]: string },
  allKeys: string[]
): string => {
  // Type-safe access to predefined colors
  const color = (predefinedColors as Record<string, string>)[key];
  if (color) {
    return color;
  }
  // Find index in sorted keys and assign from palette
  const sortedKeys = [...allKeys].sort();
  const index = sortedKeys.indexOf(key);
  return COLOR_PALETTE[index % COLOR_PALETTE.length];
};

/**
 * Get color with fallback support
 * @param value - Value to get color for
 * @param colorMap - Map of values to colors
 * @param fallbackPalette - Optional fallback palette (defaults to COLOR_PALETTE)
 * @returns Color string
 */
export const getColorWithFallback = (
  value: string,
  colorMap: Record<string, string>,
  fallbackPalette: readonly string[] = COLOR_PALETTE
): string => {
  // Return mapped color if it exists
  if (colorMap[value]) {
    return colorMap[value];
  }
  
  // Use fallback palette
  const sortedValues = Object.keys(colorMap).sort();
  const index = sortedValues.length + (value.charCodeAt(0) % fallbackPalette.length);
  return fallbackPalette[index % fallbackPalette.length];
};

/**
 * Get severity color
 * @param severity - Severity level
 * @returns Color string or default gray
 */
export const getSeverityColor = (severity: string): string => {
  return SEVERITY_COLORS[severity as keyof typeof SEVERITY_COLORS] || '#9e9e9e';
};

/**
 * Get status color for solutions
 * @param status - Status value
 * @returns Color string or default gray
 */
export const getSolutionStatusColor = (status: string): string => {
  return SOLUTION_STATUS_COLORS[status as keyof typeof SOLUTION_STATUS_COLORS] || '#9e9e9e';
};

/**
 * Get priority color
 * @param priority - Priority level
 * @returns Color string or default gray
 */
export const getPriorityColor = (priority: string): string => {
  return PRIORITY_COLORS[priority as keyof typeof PRIORITY_COLORS] || '#9e9e9e';
};

/**
 * Get category color
 * @param category - Category value
 * @returns Color string or default gray
 */
export const getCategoryColor = (category: string): string => {
  return CATEGORY_COLORS[category as keyof typeof CATEGORY_COLORS] || '#9e9e9e';
};

/**
 * Get source color
 * @param source - Source value
 * @returns Color string or default gray
 */
export const getSourceColor = (source: string): string => {
  return SOURCE_COLORS[source as keyof typeof SOURCE_COLORS] || '#9e9e9e';
};
